// ─── Animation Style Functions ───────────────────────────────────────────────

export function getEnterStyle(animation, progress) {
  const { enter = 'fadeIn' } = animation || {};
  const p = Math.min(1, Math.max(0, progress));

  switch (enter) {
    case 'fadeIn':
      return { opacity: p };
    case 'slideUp':
      return { opacity: p, transform: `translateY(${(1 - p) * 40}px)` };
    case 'slideDown':
      return { opacity: p, transform: `translateY(${(1 - p) * -40}px)` };
    case 'slideLeft':
      return { opacity: p, transform: `translateX(${(1 - p) * 60}px)` };
    case 'slideRight':
      return { opacity: p, transform: `translateX(${(1 - p) * -60}px)` };
    case 'scaleUp':
      return { opacity: p, transform: `scale(${0.3 + p * 0.7})` };
    case 'scaleDown':
      return { opacity: p, transform: `scale(${1.5 - p * 0.5})` };
    case 'rotateIn':
      return { opacity: p, transform: `rotate(${(1 - p) * -15}deg) scale(${0.8 + p * 0.2})` };
    case 'blurIn':
      return { opacity: p, filter: `blur(${(1 - p) * 8}px)` };
    case 'bounceIn': {
      const bounce = p < 0.6 ? p / 0.6 : 1 + Math.sin((p - 0.6) / 0.4 * Math.PI) * 0.15;
      return { opacity: Math.min(1, p * 1.5), transform: `scale(${bounce})` };
    }
    case 'typewriter':
      return { opacity: 1, clipPath: `inset(0 ${(1 - p) * 100}% 0 0)` };
    case 'glitch': {
      const jitter = p < 1 ? (Math.random() - 0.5) * (1 - p) * 6 : 0;
      return { opacity: p > 0.1 ? 1 : 0, transform: `translate(${jitter}px, ${jitter}px)` };
    }
    case 'flipIn':
      return { opacity: p, transform: `perspective(400px) rotateX(${(1 - p) * 90}deg)` };
    case 'elasticIn': {
      const elastic = p === 1 ? 1 : -Math.pow(2, -10 * p) * Math.sin((p * 10 - 0.75) * (2 * Math.PI) / 3) + 1;
      return { opacity: Math.min(1, p * 2), transform: `scale(${elastic})` };
    }
    default:
      return { opacity: p };
  }
}

export function getExitStyle(animation, progress) {
  const { exit = 'fadeOut' } = animation || {};
  const p = Math.min(1, Math.max(0, progress)); // 0 = start of exit, 1 = fully gone

  switch (exit) {
    case 'fadeOut':
      return { opacity: 1 - p };
    case 'slideUp':
      return { opacity: 1 - p, transform: `translateY(${p * -40}px)` };
    case 'slideDown':
      return { opacity: 1 - p, transform: `translateY(${p * 40}px)` };
    case 'slideLeft':
      return { opacity: 1 - p, transform: `translateX(${p * -60}px)` };
    case 'slideRight':
      return { opacity: 1 - p, transform: `translateX(${p * 60}px)` };
    case 'scaleDown':
      return { opacity: 1 - p, transform: `scale(${1 - p * 0.7})` };
    case 'blurOut':
      return { opacity: 1 - p, filter: `blur(${p * 8}px)` };
    default:
      return { opacity: 1 - p };
  }
}

// ─── Presets ─────────────────────────────────────────────────────────────────

const textOverlayPresets = [
  // Dynamic
  {
    id: 'bold-title', name: 'Bold Title', category: 'dynamic',
    style: { fontFamily: 'Inter, sans-serif', fontSize: 48, fontWeight: 900, color: '#ffffff', background: '', textShadow: '2px 2px 8px rgba(0,0,0,0.6)', letterSpacing: 1, textTransform: 'uppercase', textAlign: 'center', lineHeight: 1.2, borderRadius: 0, padding: '0' },
    animation: { enter: 'scaleUp', exit: 'fadeOut', enterDuration: 0.6, exitDuration: 0.4 },
  },
  {
    id: 'slide-up', name: 'Slide Up', category: 'dynamic',
    style: { fontFamily: 'Inter, sans-serif', fontSize: 36, fontWeight: 700, color: '#ffffff', background: '', textShadow: '0 2px 10px rgba(0,0,0,0.5)', letterSpacing: 0, textTransform: 'none', textAlign: 'center', lineHeight: 1.3, borderRadius: 0, padding: '0' },
    animation: { enter: 'slideUp', exit: 'slideDown', enterDuration: 0.5, exitDuration: 0.4 },
  },
  {
    id: 'bounce-pop', name: 'Bounce Pop', category: 'dynamic',
    style: { fontFamily: 'Inter, sans-serif', fontSize: 40, fontWeight: 800, color: '#fbbf24', background: '', textShadow: '0 0 20px rgba(251,191,36,0.4)', letterSpacing: 0, textTransform: 'none', textAlign: 'center', lineHeight: 1.3, borderRadius: 0, padding: '0' },
    animation: { enter: 'bounceIn', exit: 'scaleDown', enterDuration: 0.7, exitDuration: 0.3 },
  },
  {
    id: 'elastic-entry', name: 'Elastic', category: 'dynamic',
    style: { fontFamily: 'Inter, sans-serif', fontSize: 38, fontWeight: 700, color: '#a78bfa', background: '', textShadow: '0 0 15px rgba(167,139,250,0.5)', letterSpacing: 1, textTransform: 'none', textAlign: 'center', lineHeight: 1.3, borderRadius: 0, padding: '0' },
    animation: { enter: 'elasticIn', exit: 'fadeOut', enterDuration: 0.8, exitDuration: 0.4 },
  },
  // Minimal
  {
    id: 'clean-fade', name: 'Clean Fade', category: 'minimal',
    style: { fontFamily: 'Inter, sans-serif', fontSize: 28, fontWeight: 500, color: '#ffffff', background: '', textShadow: 'none', letterSpacing: 0.5, textTransform: 'none', textAlign: 'center', lineHeight: 1.4, borderRadius: 0, padding: '0' },
    animation: { enter: 'fadeIn', exit: 'fadeOut', enterDuration: 0.6, exitDuration: 0.5 },
  },
  {
    id: 'typewriter', name: 'Typewriter', category: 'minimal',
    style: { fontFamily: 'Courier New, monospace', fontSize: 26, fontWeight: 600, color: '#e2e8f0', background: '', textShadow: 'none', letterSpacing: 1, textTransform: 'none', textAlign: 'left', lineHeight: 1.4, borderRadius: 0, padding: '0' },
    animation: { enter: 'typewriter', exit: 'fadeOut', enterDuration: 1.0, exitDuration: 0.3 },
  },
  {
    id: 'blur-reveal', name: 'Blur Reveal', category: 'minimal',
    style: { fontFamily: 'Inter, sans-serif', fontSize: 30, fontWeight: 600, color: '#ffffff', background: '', textShadow: '0 2px 10px rgba(0,0,0,0.3)', letterSpacing: 0, textTransform: 'none', textAlign: 'center', lineHeight: 1.3, borderRadius: 0, padding: '0' },
    animation: { enter: 'blurIn', exit: 'blurOut', enterDuration: 0.6, exitDuration: 0.4 },
  },
  {
    id: 'gentle-slide', name: 'Gentle Slide', category: 'minimal',
    style: { fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 400, color: '#f8fafc', background: '', textShadow: 'none', letterSpacing: 0, textTransform: 'none', textAlign: 'center', lineHeight: 1.5, borderRadius: 0, padding: '0' },
    animation: { enter: 'slideRight', exit: 'slideLeft', enterDuration: 0.5, exitDuration: 0.4 },
  },
  // Creative
  {
    id: 'neon-glow', name: 'Neon Glow', category: 'creative',
    style: { fontFamily: 'Inter, sans-serif', fontSize: 36, fontWeight: 700, color: '#22d3ee', background: '', textShadow: '0 0 7px #22d3ee, 0 0 20px #22d3ee, 0 0 40px #0891b2', letterSpacing: 2, textTransform: 'uppercase', textAlign: 'center', lineHeight: 1.3, borderRadius: 0, padding: '0' },
    animation: { enter: 'fadeIn', exit: 'fadeOut', enterDuration: 0.4, exitDuration: 0.4 },
  },
  {
    id: 'glitch-text', name: 'Glitch', category: 'creative',
    style: { fontFamily: 'Inter, sans-serif', fontSize: 34, fontWeight: 800, color: '#ef4444', background: '', textShadow: '2px 0 #3b82f6, -2px 0 #22c55e', letterSpacing: 3, textTransform: 'uppercase', textAlign: 'center', lineHeight: 1.3, borderRadius: 0, padding: '0' },
    animation: { enter: 'glitch', exit: 'fadeOut', enterDuration: 0.8, exitDuration: 0.3 },
  },
  {
    id: 'flip-card', name: 'Flip Card', category: 'creative',
    style: { fontFamily: 'Inter, sans-serif', fontSize: 32, fontWeight: 700, color: '#ffffff', background: 'rgba(0,0,0,0.6)', textShadow: 'none', letterSpacing: 0, textTransform: 'none', textAlign: 'center', lineHeight: 1.3, borderRadius: 8, padding: '12px 24px' },
    animation: { enter: 'flipIn', exit: 'scaleDown', enterDuration: 0.6, exitDuration: 0.4 },
  },
  {
    id: 'rotate-reveal', name: 'Rotate Reveal', category: 'creative',
    style: { fontFamily: 'Inter, sans-serif', fontSize: 34, fontWeight: 700, color: '#fbbf24', background: '', textShadow: '0 2px 10px rgba(0,0,0,0.5)', letterSpacing: 1, textTransform: 'none', textAlign: 'center', lineHeight: 1.3, borderRadius: 0, padding: '0' },
    animation: { enter: 'rotateIn', exit: 'fadeOut', enterDuration: 0.6, exitDuration: 0.4 },
  },
  // Boxed
  {
    id: 'dark-box', name: 'Dark Box', category: 'boxed',
    style: { fontFamily: 'Inter, sans-serif', fontSize: 28, fontWeight: 600, color: '#ffffff', background: 'rgba(0,0,0,0.8)', textShadow: 'none', letterSpacing: 0, textTransform: 'none', textAlign: 'center', lineHeight: 1.4, borderRadius: 8, padding: '10px 20px' },
    animation: { enter: 'scaleUp', exit: 'scaleDown', enterDuration: 0.4, exitDuration: 0.3 },
  },
  {
    id: 'gradient-box', name: 'Gradient Box', category: 'boxed',
    style: { fontFamily: 'Inter, sans-serif', fontSize: 30, fontWeight: 700, color: '#ffffff', background: 'linear-gradient(135deg, #667eea, #764ba2)', textShadow: 'none', letterSpacing: 0, textTransform: 'none', textAlign: 'center', lineHeight: 1.3, borderRadius: 10, padding: '12px 24px' },
    animation: { enter: 'slideUp', exit: 'slideDown', enterDuration: 0.5, exitDuration: 0.4 },
  },
  {
    id: 'glass-panel', name: 'Glass Panel', category: 'boxed',
    style: { fontFamily: 'Inter, sans-serif', fontSize: 26, fontWeight: 600, color: '#ffffff', background: 'rgba(255,255,255,0.15)', textShadow: '0 1px 3px rgba(0,0,0,0.3)', letterSpacing: 0.5, textTransform: 'none', textAlign: 'center', lineHeight: 1.4, borderRadius: 12, padding: '10px 20px' },
    animation: { enter: 'fadeIn', exit: 'fadeOut', enterDuration: 0.5, exitDuration: 0.4 },
  },
  {
    id: 'outline-box', name: 'Outline Box', category: 'boxed',
    style: { fontFamily: 'Inter, sans-serif', fontSize: 28, fontWeight: 700, color: '#ffffff', background: '', textShadow: '-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff', letterSpacing: 1, textTransform: 'uppercase', textAlign: 'center', lineHeight: 1.3, borderRadius: 0, padding: '0' },
    animation: { enter: 'slideLeft', exit: 'slideRight', enterDuration: 0.5, exitDuration: 0.4 },
  },
];

export default textOverlayPresets;
